import {Component} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {from} from 'rxjs';
import {concatMap, finalize, tap} from 'rxjs/operators';
import {TaskService} from '../../../../services/task.service';
import {SyncTask, TaskSyncImportResult, TaskSyncManifest} from '../../../../types/task-sync-manifest.class';

@Component({
  selector: 'portal-sync-import-dialog',
  templateUrl: './sync-import-dialog.component.html',
  styleUrls: ['./sync-import-dialog.component.scss']
})
export class SyncImportDialogComponent {
  manifest: TaskSyncManifest;
  files: File[] = [];
  manifestError = '';
  fileError = '';
  importing = false;
  progress = 0;
  results: TaskSyncImportResult[] = [];

  constructor(private taskService: TaskService,
              private dialogRef: MatDialogRef<SyncImportDialogComponent>,
              private popup: MatSnackBar) {
  }

  get ready(): boolean {
    return !!this.manifest && !this.manifestError && !this.fileError && this.files.length > 0;
  }

  get tasksWithFiles(): { task: SyncTask, file: File }[] {
    if (!this.manifest) {
      return [];
    }
    return this.manifest.tasks.map(task => ({
      task,
      file: this.files.find(file => file.name === task.originalFilename)
    })).filter(row => !!row.file);
  }

  onManifestPicked(files: FileList): void {
    this.manifestError = '';
    const file = files?.item(0);
    if (!file) {
      this.manifest = undefined;
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        if (!parsed?.tasks?.length || parsed.formatVersion !== 1) {
          this.manifestError = 'The metadata file is not a supported sync manifest.';
          this.manifest = undefined;
          return;
        }
        this.manifest = new TaskSyncManifest(parsed);
        this.validateFiles();
      } catch (err) {
        this.manifestError = 'The metadata file could not be parsed.';
        this.manifest = undefined;
      }
    };
    reader.readAsText(file);
  }

  onFilesPicked(files: FileList): void {
    this.files = Array.from(files || []);
    this.validateFiles();
  }

  import(): void {
    if (!this.ready || this.importing) {
      return;
    }

    this.importing = true;
    this.progress = 0;
    this.results = [];

    const rows = this.tasksWithFiles;
    from(rows).pipe(
      concatMap(row => this.taskService.importSyncFile(this.manifest, row.task, row.file).pipe(
        tap(result => {
          this.results.push(result);
          this.progress = Math.round((this.results.length / rows.length) * 100);
        })
      )),
      finalize(() => this.importing = false)
    ).subscribe(() => {
    }, () => {
      this.popup.open('Sync import failed.', 'OK', {
        panelClass: ['failure']
      });
    }, () => {
      this.popup.open('Sync import finished.', 'OK', {
        duration: 5000,
        panelClass: ['success']
      });
      this.dialogRef.close(true);
    });
  }

  close(): void {
    this.dialogRef.close(false);
  }

  private validateFiles(): void {
    this.fileError = '';
    if (!this.manifest || !this.files.length) {
      return;
    }

    const filesByName = new Map<string, File[]>();
    this.files.forEach(file => {
      const current = filesByName.get(file.name) || [];
      current.push(file);
      filesByName.set(file.name, current);
    });

    const missing = this.manifest.tasks
      .filter(task => !filesByName.has(task.originalFilename))
      .map(task => task.originalFilename);
    const duplicate = Array.from(filesByName.entries())
      .filter(([, values]) => values.length > 1)
      .map(([name]) => name);
    const sizeMismatch = this.manifest.tasks
      .filter(task => {
        const file = filesByName.get(task.originalFilename)?.[0];
        return file && task.fileLength !== null && task.fileLength !== undefined && file.size !== task.fileLength;
      })
      .map(task => task.originalFilename);

    if (missing.length) {
      this.fileError = `Missing files: ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? '...' : ''}`;
    } else if (duplicate.length) {
      this.fileError = `Duplicate selected files: ${duplicate.slice(0, 5).join(', ')}${duplicate.length > 5 ? '...' : ''}`;
    } else if (sizeMismatch.length) {
      this.fileError = `File sizes do not match metadata: ${sizeMismatch.slice(0, 5).join(', ')}${sizeMismatch.length > 5 ? '...' : ''}`;
    }
  }
}
